import { StandardEvents } from '@shopify/events';

/**
 * Fecha estimada de entrega.
 *
 * Suma preparación, taller y tránsito a partir de hoy, saltando fines de
 * semana y festivos si así está configurado. Ninguna regla está escrita aquí:
 * todas llegan como atributos desde los ajustes del bloque.
 */
class MrDelivery extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const section = this.closest('.shopify-section');
    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#controller.signal,
    });
    // Grabado como servicio aparte: el bloque de grabado avisa en cada cambio
    section?.addEventListener('mr:extras-changed', this.#render, { signal: this.#controller.signal });

    this.#render();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get #isEngraved() {
    return this.#serviceEngraved || this.dataset.engravingValue !== this.dataset.engravingNone;
  }

  /** Grabado elegido en el bloque de grabado en modo servicio. */
  get #serviceEngraved() {
    const section = this.closest('.shopify-section');
    for (const engraving of section?.querySelectorAll('mr-engraving[data-mr-service="true"]') ?? []) {
      if (engraving instanceof HTMLElement && Number(engraving.dataset.mrServicePrice) > 0) return true;
    }
    return false;
  }

  /**
   * Días de taller del tiempo elegido, según la tabla del editor. Si el
   * producto no tiene opción de tiempo de grabado, o el valor no está en la
   * tabla, se usan los días de taller por defecto.
   */
  get #workshopDays() {
    if (!this.#isEngraved) return 0;

    const wanted = normalize(this.dataset.timeValue ?? '');

    if (wanted) {
      for (const line of (this.dataset.workshopDays ?? '').split('\n')) {
        const [value, days] = line.split('|');
        if (value && normalize(value) === wanted) return Number(days) || 0;
      }
    }

    return Number(this.dataset.workshopDefault) || 0;
  }

  get #holidays() {
    return new Set(
      (this.dataset.holidays ?? '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
    );
  }

  #render = () => {
    const headline = this.querySelector('[data-mr-delivery-headline]');
    const note = this.querySelector('[data-mr-delivery-note]');

    const prep = this.#isEngraved ? this.#workshopDays : Number(this.dataset.prepDays) || 0;
    const transitMin = Number(this.dataset.transitMin) || 0;
    const transitMax = Number(this.dataset.transitMax) || 0;

    // Se cuenta desde hoy. Solo con la hora de corte activada, pasada esa hora
    // el pedido ya no sale hoy y se cuenta desde mañana.
    const start = new Date();
    const cutoff = Number(this.dataset.cutoffHour);
    if (this.dataset.useCutoff === 'true' && Number.isFinite(cutoff) && start.getHours() >= cutoff) {
      start.setDate(start.getDate() + 1);
    }

    const skipWeekends = this.dataset.skipWeekends === 'true';
    const holidays = this.#holidays;

    const from = addWorkingDays(start, prep + transitMin, skipWeekends, holidays);
    const to = addWorkingDays(start, prep + transitMax, skipWeekends, holidays);

    if (headline) {
      headline.textContent = (this.dataset.template ?? '')
        .replace('[desde]', formatDate(from, from.getMonth() !== to.getMonth()))
        .replace('[hasta]', formatDate(to, true));
    }

    if (note) note.textContent = (this.#isEngraved ? this.dataset.noteEngraved : this.dataset.notePlain) ?? '';
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        const fresh = detail?.html?.querySelector('mr-delivery');
        if (fresh instanceof HTMLElement) {
          this.dataset.engravingValue = fresh.dataset.engravingValue ?? '';
          this.dataset.timeValue = fresh.dataset.timeValue ?? '';
        }
        this.#render();
      })
      .catch(() => {
        /* si falla, se queda la estimación anterior */
      });
  };
}

/** @param {string} text */
function normalize(text) {
  return text
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/**
 * @param {Date} start
 * @param {number} days
 * @param {boolean} skipWeekends
 * @param {Set<string>} holidays
 */
function addWorkingDays(start, days, skipWeekends, holidays) {
  const date = new Date(start);
  let left = days;

  while (left > 0) {
    date.setDate(date.getDate() + 1);
    const weekend = date.getDay() === 0 || date.getDay() === 6;
    if (skipWeekends && weekend) continue;
    if (holidays.has(date.toISOString().slice(0, 10))) continue;
    left -= 1;
  }

  return date;
}

/**
 * @param {Date} date
 * @param {boolean} withMonth
 */
function formatDate(date, withMonth) {
  return date.toLocaleDateString('es-ES', withMonth ? { day: 'numeric', month: 'long' } : { day: 'numeric' });
}

if (!customElements.get('mr-delivery')) {
  customElements.define('mr-delivery', MrDelivery);
}
